import { get, isArray } from 'lodash';
import { denormalize, Schema } from 'normalizr';

import { isCacheStale, isCacheStaleByIds } from './cache-invalidation-utils';
import { NormalisedStoreState } from './types';

/**
 * Generic selector to pull denormalised entities based on api path.
 *
 * @param {NormalisedStoreState} normalised
 * @param {Schema} schema
 * @param {string} path
 * @returns {T | null}
 */
export const fromNormalisedStoreSelector = <T>(
  normalised: NormalisedStoreState,
  schema: Schema,
  path: string
): T | null => {
  const entity = isArray(schema) ? get(schema, '[0]._key') : get(schema, '_key');
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const ids: string[] | string | undefined = get(normalised, `requests.${entity}.${path}`);
  const stale = isCacheStale(path);

  if (!ids || stale) {
    return null;
  }

  return denormalize(ids, schema, normalised.entities);
};

/**
 * Generic selector to pull denormalised entities based on cache data.
 *
 * @note Use this method just in case you need to retrieve data from cache data,
 * but in the majority of the cases @see fromNormalisedStoreSelector is the trusted method
 * as the data is cached by api path.
 *
 * @param {NormalisedStoreState} normalised
 * @param {Schema} schema
 * @param {string[]} ids
 * @returns {T | null}
 */
export const fromNormalisedStoreCacheSelector = <T>(
  normalised: NormalisedStoreState,
  schema: Schema,
  ids: string[]
): T[] | T | null => {
  const entity = isArray(schema) ? get(schema, '[0]._key') : get(schema, '_key');
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const entities = get(normalised, `entities.${entity}`, {});
  const stale = isCacheStaleByIds(normalised, entity as unknown as string, ids);

  if (stale) {
    return null;
  }

  let result: string[] | string | null;

  if (isArray(schema)) {
    const entitiesIds: string[] = Object.keys(entities);
    result = entitiesIds.length > 0 && ids.every(id => entitiesIds.includes(id)) ? ids : [];
  } else {
    result = entities.hasOwnProperty(ids[0]) ? ids[0] : null;
  }

  return result && result.length > 0 ? denormalize(result, schema, normalised.entities) : result;
};
